import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Headset, UserPlus, Search, RefreshCw, XCircle, Trash2,
  Mail, AlertTriangle, CheckCircle2, Clock, Slash, Edit,
  Settings as SettingsIcon,
} from 'lucide-react';
import Modal from '../../components/common/Modal';
import { vrAccessAPI } from '../../services/api';
import { useAppContext } from '../../context/AppContext';
import { formatDate } from '../../utils/dateFormatter';
import './VRAccess.css';

const STATUS_META = {
  pending:  { label: 'Pending',  icon: Clock,         className: 'vr-badge vr-badge--pending' },
  invited:  { label: 'Invited',  icon: Mail,          className: 'vr-badge vr-badge--invited' },
  accepted: { label: 'Accepted', icon: CheckCircle2,  className: 'vr-badge vr-badge--accepted' },
  revoked:  { label: 'Revoked',  icon: Slash,         className: 'vr-badge vr-badge--revoked' },
  failed:   { label: 'Failed',   icon: AlertTriangle, className: 'vr-badge vr-badge--failed' },
};

const StatusBadge = ({ status, error }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span className={meta.className} title={error || meta.label}>
      <Icon size={13} />
      <span>{meta.label}</span>
    </span>
  );
};

const VRAccess = () => {
  const { showNotification } = useAppContext();
  const [users, setUsers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [channelFilter, setChannelFilter] = useState('All');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ channelId: '', email: '', name: '', oculusId: '' });
  const [submitting, setSubmitting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, cRes] = await Promise.all([
        vrAccessAPI.listUsers(),
        vrAccessAPI.listChannels(),
      ]);
      setUsers(uRes.data?.data || []);
      setChannels(cRes.data?.data || []);
    } catch (err) {
      showNotification('Failed to load VR access data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openInvite = () => {
    setEditing(null);
    setForm({
      channelId: channels[0]?.channelId?.replace(/-/g, '') || '',
      email: '', name: '', oculusId: '',
    });
    setInviteOpen(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({
      channelId: user.channelUuid?.replace(/-/g, '') || '',
      email: user.email,
      name: user.name,
      oculusId: user.oculusId || '',
    });
    setInviteOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name || (!editing && !form.channelId)) {
      showNotification('Email, name, and channel are required', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await vrAccessAPI.updateUser(editing.vrUserId.replace(/-/g, ''), {
          name: form.name,
          oculusId: form.oculusId || null,
        });
        showNotification('VR user updated', 'success');
      } else {
        const res = await vrAccessAPI.createUser({
          channelId: form.channelId,
          email: form.email,
          name: form.name,
          oculusId: form.oculusId || undefined,
        });
        const created = res.data?.data;
        if (created?.inviteStatus === 'failed') {
          console.error('Meta invite failed:', created.lastError);
          showNotification(`Meta invite failed: ${created.lastError || 'Unknown error'}`, 'error');
        } else {
          showNotification('Invite sent', 'success');
        }
      }
      setInviteOpen(false);
      loadAll();
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      showNotification(msg || 'Failed to save VR user', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (u) => {
    try {
      await vrAccessAPI.resendInvite(u.vrUserId.replace(/-/g, ''));
      showNotification('Invite resent', 'success');
      loadAll();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to resend invite', 'error');
    }
  };

  const handleRevoke = async (u) => {
    if (!window.confirm(`Revoke invite for ${u.email}?`)) return;
    try {
      await vrAccessAPI.revokeInvite(u.vrUserId.replace(/-/g, ''));
      showNotification('Invite revoked', 'success');
      loadAll();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to revoke', 'error');
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete VR user ${u.email}? This cannot be undone.`)) return;
    try {
      await vrAccessAPI.deleteUser(u.vrUserId.replace(/-/g, ''));
      showNotification('VR user deleted', 'success');
      loadAll();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  const filtered = users.filter((u) => {
    if (statusFilter !== 'All' && u.inviteStatus !== statusFilter) return false;
    if (channelFilter !== 'All' && u.channelUuid !== channelFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!u.email.toLowerCase().includes(q) &&
          !u.name.toLowerCase().includes(q) &&
          !(u.oculusId || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="vr-access-page">
      <header className="vr-access-header">
        <div>
          <h1><Headset size={22} /> VR Access</h1>
          <p>Invite and manage users for VR experiences distributed via Meta Developer Hub.</p>
        </div>
        <div className="vr-access-header__actions">
          <Link to="/vr-access/channels" className="btn btn--ghost">
            <SettingsIcon size={16} /> Manage Channels
          </Link>
          <button className="btn btn--primary" onClick={openInvite} disabled={!channels.length}>
            <UserPlus size={16} /> Invite VR User
          </button>
        </div>
      </header>

      {!channels.length && !loading && (
        <div className="vr-access-empty-banner">
          <AlertTriangle size={18} />
          <span>
            No VR channels configured yet. <Link to="/vr-access/channels">Add a channel</Link> first
            (Meta app id, app secret, and release channel id).
          </span>
        </div>
      )}

      <div className="vr-access-filters">
        <div className="vr-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search email, name, or Oculus ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="All">All statuses</option>
          {Object.entries(STATUS_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}>
          <option value="All">All channels</option>
          {channels.map(c => (
            <option key={c.channelId} value={c.channelId}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className="vr-access-table-wrap">
        <table className="vr-access-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Oculus ID</th>
              <th>Channel</th>
              <th>Status</th>
              <th>Invited</th>
              <th className="vr-access-table__actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="7" className="vr-access-table__empty">Loading...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="7" className="vr-access-table__empty">No VR users yet.</td></tr>
            )}
            {!loading && filtered.map((u) => (
              <tr key={u.vrUserId}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td className="vr-mono">{u.oculusId || <span className="vr-muted">—</span>}</td>
                <td>{u.channelName}</td>
                <td>
                  <StatusBadge status={u.inviteStatus} error={u.lastError} />
                  {u.inviteStatus === 'failed' && u.lastError && (
                    <div className="vr-error-detail" title={u.lastError}>{u.lastError}</div>
                  )}
                </td>
                <td>{u.invitedAt ? formatDate(u.invitedAt) : <span className="vr-muted">—</span>}</td>
                <td className="vr-access-table__actions">
                  <button className="icon-btn" title="Edit" onClick={() => openEdit(u)}>
                    <Edit size={15} />
                  </button>
                  <button
                    className="icon-btn"
                    title="Resend invite"
                    onClick={() => handleResend(u)}
                    disabled={u.inviteStatus === 'accepted'}
                  >
                    <RefreshCw size={15} />
                  </button>
                  <button
                    className="icon-btn"
                    title="Revoke invite"
                    onClick={() => handleRevoke(u)}
                    disabled={u.inviteStatus === 'revoked'}
                  >
                    <XCircle size={15} />
                  </button>
                  <button className="icon-btn icon-btn--danger" title="Delete" onClick={() => handleDelete(u)}>
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title={editing ? 'Edit VR User' : 'Invite VR User'}
        size="md"
        footer={
          <>
            <button className="btn btn--ghost" onClick={() => setInviteOpen(false)} disabled={submitting}>
              Cancel
            </button>
            <button className="btn btn--primary" onClick={submit} disabled={submitting}>
              {submitting ? 'Saving...' : (editing ? 'Save' : 'Send Invite')}
            </button>
          </>
        }
      >
        <form className="vr-form" onSubmit={submit}>
          {!editing && (
            <label className="vr-field">
              <span>VR Channel *</span>
              <select
                value={form.channelId}
                onChange={(e) => setForm(f => ({ ...f, channelId: e.target.value }))}
                required
              >
                {channels.map(c => (
                  <option key={c.channelId} value={c.channelId.replace(/-/g, '')}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="vr-field">
            <span>Email *</span>
            <input
              type="email"
              required
              disabled={!!editing}
              value={form.email}
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="user@example.com"
            />
          </label>
          <label className="vr-field">
            <span>Name *</span>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Full name"
            />
          </label>
          <label className="vr-field">
            <span>Oculus ID <span className="vr-muted">(optional)</span></span>
            <input
              type="text"
              value={form.oculusId}
              onChange={(e) => setForm(f => ({ ...f, oculusId: e.target.value }))}
              placeholder="Their Meta/Oculus handle if known"
            />
          </label>
          {!editing && (
            <p className="vr-form-hint">
              An invitation will be sent through Meta Developer Hub for the selected release channel.
            </p>
          )}
        </form>
      </Modal>
    </div>
  );
};

export default VRAccess;
